/**
 * IndexedDB Storage Adapter
 *
 * Low-level IndexedDB operations for the CacheService.
 * Provides async storage with automatic database initialization.
 */

import { log } from '../../utils/DebugLogger'

const DB_NAME = 'franchise-factory-cache'
const DB_VERSION = 1
const STORE_NAME = 'cache-entries'

export interface CacheEntry<T = unknown> {
  key: string
  value: T
  expiry: number
  createdAt: number
  updatedAt: number
}

export interface AdapterStats {
  totalEntries: number
  expiredEntries: number
  validEntries: number
  keys: string[]
}

class IndexedDBAdapter {
  private db: IDBDatabase | null = null
  private dbPromise: Promise<IDBDatabase> | null = null

  async init(): Promise<IDBDatabase> {
    if (this.db) return this.db

    if (this.dbPromise) return this.dbPromise

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = (event) => {
        const error = (event.target as IDBOpenDBRequest).error
        log('IndexedDBAdapter', 'init', 'Database error', error)
        reject(error)
      }

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result
        log('IndexedDBAdapter', 'init', 'Database opened successfully')
        resolve(this.db)
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' })
          store.createIndex('expiry', 'expiry', { unique: false })
          log('IndexedDBAdapter', 'init', 'Object store created')
        }
      }
    })

    return this.dbPromise
  }

  async get<T = unknown>(key: string): Promise<CacheEntry<T> | null> {
    const db = await this.init()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.get(key)

      request.onsuccess = () => {
        const result = request.result as CacheEntry<T> | undefined
        if (result) {
          log('IndexedDBAdapter', 'get', `Found: ${key}`, { expiry: result.expiry })
        }
        resolve(result || null)
      }

      request.onerror = (event) => {
        const error = (event.target as IDBRequest).error
        log('IndexedDBAdapter', 'get', `Error: ${key}`, error)
        reject(error)
      }
    })
  }

  async set<T = unknown>(key: string, value: T, expiry: number): Promise<void> {
    const db = await this.init()

    const entry: CacheEntry<T> = {
      key,
      value,
      expiry,
      createdAt: Date.now(),
      updatedAt: Date.now()
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.put(entry)

      request.onsuccess = () => {
        log('IndexedDBAdapter', 'set', `Stored: ${key}`, { expiry })
        resolve()
      }

      request.onerror = (event) => {
        const error = (event.target as IDBRequest).error
        log('IndexedDBAdapter', 'set', `Error: ${key}`, error)
        reject(error)
      }
    })
  }

  async delete(key: string): Promise<void> {
    const db = await this.init()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.delete(key)

      request.onsuccess = () => {
        log('IndexedDBAdapter', 'delete', `Deleted: ${key}`)
        resolve()
      }

      request.onerror = (event) => {
        const error = (event.target as IDBRequest).error
        log('IndexedDBAdapter', 'delete', `Error: ${key}`, error)
        reject(error)
      }
    })
  }

  async deletePattern(pattern: string): Promise<number> {
    const db = await this.init()
    const regex = new RegExp('^' + pattern.replace(/\*/g, '.*') + '$')

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.openCursor()
      let deletedCount = 0

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result
        if (cursor) {
          if (regex.test(cursor.key as string)) {
            cursor.delete()
            deletedCount++
          }
          cursor.continue()
        } else {
          log('IndexedDBAdapter', 'deletePattern', `Pattern: ${pattern}`, { deletedCount })
          resolve(deletedCount)
        }
      }

      request.onerror = (event) => {
        const error = (event.target as IDBRequest).error
        log('IndexedDBAdapter', 'deletePattern', `Error: ${pattern}`, error)
        reject(error)
      }
    })
  }

  async getAll(): Promise<CacheEntry[]> {
    const db = await this.init()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.getAll()

      request.onsuccess = () => {
        resolve((request.result as CacheEntry[]) || [])
      }

      request.onerror = (event) => {
        const error = (event.target as IDBRequest).error
        log('IndexedDBAdapter', 'getAll', 'Error', error)
        reject(error)
      }
    })
  }

  async getAllKeys(): Promise<string[]> {
    const db = await this.init()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.getAllKeys()

      request.onsuccess = () => {
        resolve((request.result as string[]) || [])
      }

      request.onerror = (event) => {
        const error = (event.target as IDBRequest).error
        log('IndexedDBAdapter', 'getAllKeys', 'Error', error)
        reject(error)
      }
    })
  }

  async clear(): Promise<void> {
    const db = await this.init()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.clear()

      request.onsuccess = () => {
        log('IndexedDBAdapter', 'clear', 'All entries cleared')
        resolve()
      }

      request.onerror = (event) => {
        const error = (event.target as IDBRequest).error
        log('IndexedDBAdapter', 'clear', 'Error', error)
        reject(error)
      }
    })
  }

  async getStats(): Promise<AdapterStats> {
    const entries = await this.getAll()
    const now = Date.now()

    return {
      totalEntries: entries.length,
      expiredEntries: entries.filter(e => e.expiry < now).length,
      validEntries: entries.filter(e => e.expiry >= now).length,
      keys: entries.map(e => e.key)
    }
  }

  async cleanupExpired(): Promise<number> {
    const db = await this.init()
    const now = Date.now()

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const index = store.index('expiry')
      const range = IDBKeyRange.upperBound(now)
      const request = index.openCursor(range)
      let deletedCount = 0

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result
        if (cursor) {
          cursor.delete()
          deletedCount++
          cursor.continue()
        } else {
          log('IndexedDBAdapter', 'cleanupExpired', 'Cleanup complete', { deletedCount })
          resolve(deletedCount)
        }
      }

      request.onerror = (event) => {
        const error = (event.target as IDBRequest).error
        log('IndexedDBAdapter', 'cleanupExpired', 'Error', error)
        reject(error)
      }
    })
  }

  close(): void {
    if (this.db) {
      this.db.close()
      this.db = null
      this.dbPromise = null
      log('IndexedDBAdapter', 'close', 'Database closed')
    }
  }
}

export const indexedDBAdapter = new IndexedDBAdapter()
export { IndexedDBAdapter }
